import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useOnboarding } from '../../../contexts/onboarding.context';
import OnbFrame from '../_shared/OnbFrame';
import { MbPhoneField, MbTextField } from '../../../components/MbTextFields';
import { COLORS } from '../_shared/tokens';
import { useUser } from '../../../contexts/user.context';
import { mobilizeiUsersService } from '../../../services/mobilizei/users.service';
import _ from 'lodash';
import mixpanel from 'mixpanel-browser';
import { mixpanelEvents, onboardingSteps } from '../../../utils/constants';
import { DEFAULT_COUNTRY_CODE, getCountryByCode } from '../../../utils/countries';
import { showApiError } from '../../../utils/errors';
import ecommercePlatforms from '../../../utils/ecommercePlatforms';
import useCreateStore, { buildAuthorizedStorePayload, hasPlatformAuthorization } from '../../../hooks/createStore.hook';

const SOURCES = [
    { id: 'platform', Icon: StorefrontOutlinedIcon, label: 'Na minha plataforma de e-commerce (Ex.: Nuvemshop, Tray, Loja Integrada...)' },
    { id: 'instagram', Icon: PhotoCameraOutlinedIcon, label: 'Instagram' },
    { id: 'referral', Icon: GroupOutlinedIcon, label: 'Indicação' },
    { id: 'google', Icon: SearchIcon, label: 'Google' },
    { id: 'other', Icon: MoreHorizIcon, label: 'Outro' }
];

// Sources that ask for a detail once selected: the screen state they put the
// step in, and the field revealed with it. The answer is stored in
// `discoverySourceDetail` whichever of them is selected.
const SOURCE_DETAILS = {
    referral: {
        state: 'referralSelected',
        label: 'Quem indicou?',
        placeholder: 'Conte pra gente'
    },
    other: {
        state: 'otherSelected',
        label: 'Onde foi?',
        placeholder: 'Conte pra gente'
    }
};

const SOURCE_DETAIL_IDS = Object.keys(SOURCE_DETAILS);

function getSourceIdByState(state) {
    return SOURCE_DETAIL_IDS.find(id => SOURCE_DETAILS[id].state === state);
}

function SourceOption({ source, selected, onClick }) {
    const { Icon, label } = source;

    return (
        <ButtonBase
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1.25,
                px: 1.75,
                py: { xs: 1.75, md: 1.5 },
                borderRadius: '10px',
                textAlign: 'left',
                width: '100%',
                minHeight: 44,
                border: selected ? `2px solid ${COLORS.green}` : `1px solid ${COLORS.border}`,
                backgroundColor: selected ? 'rgba(60,213,104,0.06)' : '#fff',
                fontWeight: selected ? 700 : 400,
                fontSize: 15
            }}
        >
            <Icon sx={{ fontSize: 18, color: selected ? COLORS.green : COLORS.textMuted, flexShrink: 0 }} />
            {label}
        </ButtonBase>
    );
}

/**
 * D0b · Boas-vindas — quick, skippable questions (step 1 of 7).
 * Selecting "Outro" or "Indicação" reveals a follow-up field (the
 * 'otherSelected' and 'referralSelected' states).
 */
export default function Profile() {
    const { goToNextStep, goToStep, screenState, setScreenState, data, setData } = useOnboarding();
    const { user } = useUser();
    const { createStore } = useCreateStore();

    // The merchant who installed the app from his platform's admin arrives with the
    // store already authorized, so there is nothing to ask on the connect step: the
    // store is created here and the onboarding goes straight to the promise.
    const isPlatformAuthorized = hasPlatformAuthorization(data);
    const platformName = data.ecommercePlatformName || ecommercePlatforms.getById(data.ecommercePlatformId)?.name;

    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            storeName: data.storeName || '',
        }
    });

    const [phoneNumber, setPhoneNumber] = useState('')
    const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE)
    const [discoverySource, setDiscoverySource] = useState('');
    const [discoverySourceDetail, setDiscoverySourceDetail] = useState('');

    const [isLoading, setIsLoading] = useState(false)
    const [isCreatingStore, setIsCreatingStore] = useState(false)

    const detailSourceId = getSourceIdByState(screenState);
    const sourceDetail = detailSourceId ? SOURCE_DETAILS[detailSourceId] : null;

    // Keep the selection in sync when the state is driven from outside (the
    // DevStateSwitcher), so each detail state highlights the source it belongs to.
    useEffect(() => {
        setDiscoverySource((current) => {
            if (detailSourceId) return detailSourceId;
            return SOURCE_DETAILS[current] ? SOURCES[0].id : current;
        });
    }, [detailSourceId]);

    const handleSelect = (source) => {
        setDiscoverySource(source.id);
        setScreenState(SOURCE_DETAILS[source.id]?.state || 'default');

        // The detail belongs to the source that asked for it, so it does not
        // carry over to another one.
        if (source.id !== discoverySource) {
            setDiscoverySourceDetail('');
        }
    };

    const onSubmit = (values) => {
        setIsLoading(true)
        const firstLetter = values.storeName.trim().charAt(0).toUpperCase();
        const logoUrl = `https://firebasestorage.googleapis.com/v0/b/mobilizei.appspot.com/o/themes%2Fclassic%2Flogos%2Flogo-${firstLetter}.png?alt=media`
        const phoneCountry = getCountryByCode(phoneCountryCode)
        const savedPhoneNumber = phoneNumber ? '+' + phoneCountry.dial + phoneNumber : ''
        const savedDiscoverySourceDetail = SOURCE_DETAILS[discoverySource] ? discoverySourceDetail : ''

        const dataToPost = _.omitBy({
            phoneNumber: savedPhoneNumber,
            discoverySource,
            discoverySourceDetail: savedDiscoverySourceDetail
        }, value => value === '' || _.isNil(value));

        mobilizeiUsersService.updateUserData(dataToPost)
            .finally(() => {
                setIsLoading(false)
            })

        setData((prev) => ({
            ...prev,
            storeName: values.storeName.trim(),
            phoneNumber: savedPhoneNumber,
            discoverySource,
            discoverySourceDetail: savedDiscoverySourceDetail,
            logoUrl: logoUrl
        }));
        
        mixpanel.track(mixpanelEvents.ONBOARDING_INITIATED, {
            'discovery_source': discoverySource,
            'discovery_source_detail': discoverySourceDetail,
            'whatsapp_informed': !!phoneNumber
        })

        if (!isPlatformAuthorized) {
            goToNextStep();
            return;
        }

        setIsCreatingStore(true);

        // Clears the platform data from the url so a refresh does not try again
        window.history.replaceState({}, '', window.location.pathname);

        createStore(buildAuthorizedStorePayload({ ...data, storeName: values.storeName.trim() }))
            .then(() => goToStep(onboardingSteps.PROMISE))
            .catch(e => {
                showApiError(e?.error);
                setIsCreatingStore(false);
            });
    }

    return (
        <OnbFrame step={1}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%', maxWidth: 690 }}>
                    <Box sx={{ textAlign: { xs: 'left', md: 'center' }, mb: 4 }}>
                        <Typography
                            component="h1"
                            sx={{ fontWeight: 700, fontSize: { xs: 26, md: 34 }, lineHeight: 1.1, letterSpacing: '-0.4px', mb: 1.25 }}
                        >
                            Boas-vindas, {user.name.split(' ')[0]}!
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.5, color: COLORS.textMuted }}>
                            Algumas perguntas rápidas. Elas nos ajudam a te atender melhor.
                        </Typography>

                        {isPlatformAuthorized && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 2,
                                    px: 1.75,
                                    py: 0.875,
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(60,213,104,0.1)'
                                }}
                            >
                                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: COLORS.green }} />
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                    {platformName ? `Sua loja ${platformName} já está conectada` : 'Sua loja já está conectada'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            border: { xs: 'none', md: `1px solid ${COLORS.border}` },
                            borderRadius: '14px',
                            p: { xs: 0, md: 3.5 }
                        }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Controller
                                name="storeName"
                                control={control}
                                rules={{ required: "O nome da sua loja é obrigatório." }}
                                render={({ field }) => (
                                    <MbTextField
                                        label="Qual é o nome da sua loja?"
                                        prefix={<StorefrontOutlinedIcon />}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={'Minha Loja'}
                                        error={errors.storeName?.message}
                                    />
                                )}
                            />
                            <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: COLORS.textMuted, mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <SmartphoneOutlinedIcon sx={{ fontSize: 14 }} />
                                Este é o nome que aparecerá no seu celular quando o app estiver instalado. Você poderá alterá-lo depois. 
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <MbPhoneField
                                label="WhatsApp"
                                labelExtra="opcional"
                                countryCode={phoneCountryCode}
                                // Changing the country clears the number, since its
                                // length and mask no longer apply.
                                onCountryCodeChange={(code) => {
                                    setPhoneCountryCode(code)
                                    setPhoneNumber('')
                                }}
                                value={phoneNumber}
                                // Stores the digits only, so the submit can prepend the dial code.
                                onValueChange={setPhoneNumber}
                            />
                            <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: COLORS.textMuted, mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <NotificationsOutlinedIcon sx={{ fontSize: 14 }} />
                                Para entrarmos em contato se necessário.
                            </Typography>
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.25 }}>
                                <Typography component="label" sx={{ fontSize: 15, fontWeight: 700 }}>
                                    Como você conheceu a Mobilizei?
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: COLORS.textMuted }}>opcional</Typography>
                            </Box>

                            <Stack spacing={1.25}>
                                <SourceOption
                                    source={SOURCES[0]}
                                    selected={discoverySource === SOURCES[0].id}
                                    onClick={() => handleSelect(SOURCES[0])}
                                />

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.25 }}>
                                    {SOURCES.slice(1).map((source) => (
                                        <SourceOption
                                            key={source.id}
                                            source={source}
                                            selected={discoverySource === source.id}
                                            onClick={() => handleSelect(source)}
                                        />
                                    ))}
                                </Box>

                                {!!sourceDetail && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <MbTextField
                                            label={sourceDetail.label}
                                            labelExtra="opcional"
                                            placeholder={sourceDetail.placeholder}
                                            value={discoverySourceDetail}
                                            onChange={(e) => setDiscoverySourceDetail(e.target.value)}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' }, mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading || isCreatingStore}
                            endIcon={isLoading || isCreatingStore ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                            fullWidth={false}
                            sx={{ width: { xs: '100%', md: 'auto' }, py: { xs: 1.75, md: 1.5 } }}
                        >
                            {isCreatingStore ? 'preparando seu app...' : isLoading ? 'salvando...' : 'continuar'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </OnbFrame>
    );
}
